import { HttpErrorResponse } from '@angular/common/http';
import {
	Component,
	ElementRef,
	HostListener,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { map, shareReplay, switchMap, take, tap } from 'rxjs';
import { SubSink } from 'subsink';

// --- PrimeNG ---
import { MenuItem } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';

// --- Components ---
import { SearchBoxComponent } from '@components/search-box/search-box.component';

// --- Facades ---
import { AppearanceFacade } from '@facades/appearance.facade';
import { AuthFacade } from '@facades/auth.facade';
import { CartFacade } from '@facades/cart.facade';
import { CategoriesFacade } from '@facades/categories.facade';
import { MenuLayoutFacade } from '@facades/menu-layout.facade';
import { ParametersFacade } from '@facades/parameters.facade';
import { StoresFacade } from '@facades/stores.facade';
import { TenantsFacade } from '@facades/tenants.facade';

// --- Services ---
import { SharedEventService } from '@services/shared-event.service';
import { UnreadOrderMsgsService } from '../../services/unread-order-msgs.service';

// --- Utils ---
import { getNameLabel } from '@utils/name-label.util';

// --- Interfaces ---
import type { ICategory } from '@interfaces/category';
import { EUserProfileIds, type IUser } from '@interfaces/user';

interface CustomMenuItem extends MenuItem {
	roles: EUserProfileIds[];
}

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
	private subs = new SubSink();
	isSmallScreen = false;

	@ViewChild('planTooltip', { static: false }) planTooltip!: OverlayPanel;

	readonly authState$ = this.authFacade.authState$;

	accountOptions: CustomMenuItem[] = [
		{
			label: 'Minha conta',
			icon: 'fa-solid fa-user',
			routerLink: ['account'],
			roles: [
				EUserProfileIds.CUSTOMER,
				EUserProfileIds.CUSTOMER_ADMIN,
				EUserProfileIds.APPLICATION_ADMIN,
				EUserProfileIds.PORTAL_MANAGER,
				EUserProfileIds.SELLER,
			],
		},
		{
			label: 'Meus pedidos',
			icon: 'fa-solid fa-bag-shopping',
			routerLink: ['account', 'orders'],
			roles: [EUserProfileIds.CUSTOMER, EUserProfileIds.CUSTOMER_ADMIN],
		},
		{
			label: 'Configurações',
			icon: 'fa-solid fa-gear',
			routerLink: ['preferences/records', 'company-info'],
			roles: [EUserProfileIds.CUSTOMER_ADMIN],
		},
		{
			label: 'Pedidos',
			icon: 'fa-solid fa-bag-shopping',
			routerLink: ['dashboard', 'orders'],
			roles: [EUserProfileIds.APPLICATION_ADMIN, EUserProfileIds.SELLER],
		},
		{
			label: 'Dashboard',
			icon: 'fa-solid fa-chart-line',
			routerLink: ['dashboard'],
			badge: !this.showDashboard ? 'PRO' : '',
			disabled: !this.showDashboard,
			roles: [EUserProfileIds.APPLICATION_ADMIN, EUserProfileIds.SELLER],
		},
		{
			label: 'Solicitações',
			icon: 'fa-solid fa-person-circle-question',
			routerLink: ['access-requests'],
			roles: [EUserProfileIds.APPLICATION_ADMIN],
		},
		{
			label: 'Sair',
			icon: 'fa-solid fa-arrow-right-from-bracket',
			command: () => this.logout(),
			roles: [
				EUserProfileIds.CUSTOMER,
				EUserProfileIds.CUSTOMER_ADMIN,
				EUserProfileIds.APPLICATION_ADMIN,
				EUserProfileIds.PORTAL_MANAGER,
				EUserProfileIds.SELLER,
			],
		},
	];
	autocompleteOverlayRef!: OverlayPanel;
	badge: number = 0;
	categories: ICategory[] = [];
	elements = ['#dashboard__menu'];
	hasPackingFeature: boolean = this.tenantsFacade.hasFlag(
		'enableProductPacking'
	);
	isNotPortalManager: boolean = false;
	isLoggingOut = false;
	isSearching = false;
	items!: MenuItem[];
	search: string = '';
	searchInputRef!: SearchBoxComponent;
	settingsButton!: { label: string; icon: string; routerLink: string[] };
	showClientAreaButton = this.tenantsFacade.hasModule('backoffice');
	showSettingsButton: boolean = false;
	subcategories: ICategory[] = [];
	showMessagesTab = false;

	readonly parameters = this.parametersFacade.parameters;
	unreadOrderMessages$ = this.unreadOrderMsgsService.unreadMessages$;

	constructor(
		private _elementRef: ElementRef,
		private _router: Router,
		private appearanceFacade: AppearanceFacade,
		private authFacade: AuthFacade,
		private cartFacade: CartFacade,
		private categoriesFacade: CategoriesFacade,
		private menuLayoutFacade: MenuLayoutFacade,
		private parametersFacade: ParametersFacade,
		private sharedEventService: SharedEventService,
		private storeFacade: StoresFacade,
		private tenantsFacade: TenantsFacade,
		public unreadOrderMsgsService: UnreadOrderMsgsService
	) {
		this.checkScreenSize();
	}

	@HostListener('window:resize', [])
	checkScreenSize() {
		this.isSmallScreen = window.innerWidth <= 768;
	}

	ngOnInit() {
		this.initializeSubscriptions();
		this.allowPreferences();
		this.showMessagesTab = this.parameters?.enableOrderMessages ? true : false;
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
		window.removeEventListener('scroll', this.hideTooltip.bind(this));
	}

	private get searchBoxInput() {
		return this.searchInputRef?.searchInputRef.nativeElement;
	}

	private get searchHasFocus() {
		return document.activeElement === this.searchBoxInput;
	}

	get appearance() {
		const { logo, useTheme } = this.appearanceFacade;
		return { logo, useTheme };
	}

	get cartTotalItems() {
		return this.cartFacade.totalItems || 0;
	}

	get currentStore() {
		return this.storeFacade.currentStore;
	}

	get menuLayout() {
		return this.menuLayoutFacade.menuLayout;
	}

	get searchBoxClientRect() {
		return this.searchBoxInput?.getBoundingClientRect() || { width: 0 };
	}

	get stockDecimalPlaces() {
		return (this.parametersFacade.parameters?.stockDecimalPlaces || 0) > 0
			? 1
			: 0;
	}

	private initializeSubscriptions() {
		this.subs.add(
			this.authFacade.authState$.pipe(take(1)).subscribe(({ user }) => {
				const isPortalManager =
					user?.profileId === EUserProfileIds.PORTAL_MANAGER;
				if (isPortalManager) return;

				this.isNotPortalManager = true;
				this.getCategories();

				this.subs.add(
					this.sharedEventService
						.receiveEvent()
						.pipe(switchMap(() => this.getCategories()))
						.subscribe()
				);
			})
		);
	}

	private showTooltip(event: any) {
		this.planTooltip?.toggle(event);
	}

	private hideTooltip() {
		this.planTooltip?.hide();
	}

	private getCategories() {
		return this.categoriesFacade.getAllCategories().pipe(
			map(res => res.result),
			tap({
				next: res => {
					this.categories = res.filter(category => category.parentId === null);
					this.categories = this.menuLayout?.customizeCategoriesMenu
						? this.getCustomMenu()
						: this.categories;
					this.subcategories = res.filter(category => category.parentId !== 0);

					this.appearanceFacade.showMenu = Boolean(this.categories.length);
					this.items = [];
					this.menu();
				},
				error: err => {
					if (err instanceof HttpErrorResponse) {
						const isUnauthorized = err.status === 401;
						if (isUnauthorized) this._router.navigate(['auth']);
					}
				},
			})
		);
	}

	private getCustomMenu() {
		const categories = this.categories
			.filter(category => category.exhibitionOrder !== null)
			.sort((a, b) => a.exhibitionOrder - b.exhibitionOrder);

		return categories;
	}

	private menu() {
		let super_obj = {} as any;
		const number = 5;
		const condition = this.categories.length > number;

		let fixedCategories: any = [];

		const objCategory = () => {
			this.categories.forEach((category: any) => {
				let obj = {} as any;

				let obj_fixed = {} as any;
				if (condition && fixedCategories.length < number) {
					obj_fixed.label = category.name;
					(obj_fixed.routerLink = ['/']),
						(obj_fixed.queryParams = { category: category.id }),
						(obj_fixed.target = '_self'),
						(obj_fixed.styleClass = 'menu__subcategories'),
						(obj_fixed.items = []);
				}

				obj.label = category.name;
				(obj.routerLink = ['/']),
					(obj.queryParams = { category: category.id }),
					(obj.styleClass = 'menu__subcategories'),
					(obj.target = '_self'),
					(obj.items = []);

				if (!condition) obj.target = '_self';
				obj.items = [];
				this.subcategories.forEach((subcategory: any) => {
					if (subcategory.parentId === category.id)
						obj.items.push({
							label: subcategory.name,
							routerLink: ['/'],
							styleClass: 'subcategory__menu__all',
							queryParams: { category: subcategory.id },
						});
					if (
						condition &&
						fixedCategories.length < number &&
						subcategory.parentId === category.id
					)
						obj_fixed.items?.push({
							label: subcategory.name,
							routerLink: ['/'],
							styleClass: 'subcategory__menu',
							queryParams: { category: subcategory.id },
						});
				});
				condition ? super_obj.items.push(obj) : this.items.push(obj);
				condition && Object.keys(obj_fixed).length > 0
					? fixedCategories.push(obj_fixed)
					: null;
			});
			condition ? this.items.push(super_obj) : null;
			condition && fixedCategories.length > 0
				? this.items.push(...fixedCategories)
				: null;

			this.items = this.items.map((category: any) => {
				if (category.items.length === 0) delete category.items;
				else {
					category.items.map((insideCategory: any) => {
						if (insideCategory?.items?.length === 0)
							delete insideCategory.items;
					});
				}
				return category;
			});
		};

		if (condition) {
			super_obj.label = 'Todas Categorias';
			super_obj.styleClass = 'menu__all__categories';
			super_obj.items = [];
		}

		objCategory();
	}

	private allowPreferences() {
		this.subs.add(
			this.authState$.subscribe(({ user }) => {
				if (!user) return;

				this.accountOptions = this.createMenuForUser(user);

				// const isAdmin = user.profileId === EUserProfileIds.CUSTOMER_ADMIN;
				const isAAdmin = user.profileId === EUserProfileIds.APPLICATION_ADMIN;
				const isSeller = user.profileId === EUserProfileIds.SELLER;

				this.showSettingsButton = isAAdmin || isSeller;

				this.settingsButton = {
					label: 'Configurações',
					icon: 'fa-solid fa-gear',
					routerLink: ['preferences'],
				};
			})
		);
	}

	private createMenuForUser(user: IUser): CustomMenuItem[] {
		return this.accountOptions.filter(item =>
			item.roles.includes(user.profileId)
		);
	}

	checkHasFeatures() {
		for (const element of this.elements) {
			const nativeElement =
				this._elementRef.nativeElement.querySelector(element);

			nativeElement
				?.closest('li')
				?.addEventListener('mouseenter', this.showTooltip.bind(this));
		}

		window.addEventListener('scroll', this.hideTooltip.bind(this));
	}

	getNameLabel(name: string | undefined) {
		return getNameLabel(name as string);
	}

	handleShowAutocomplete(event?: Event) {
		this.autocompleteOverlayRef.toggle(this.searchHasFocus, event?.target);
	}

	login() {
		this.subs.add(
			this.authFacade.logout().subscribe(() => this._router.navigate(['/auth']))
		);
	}

	logout() {
		this.isLoggingOut = true;

		this.subs.add(
			this.authFacade
				.logout()
				.pipe(shareReplay({ refCount: true }))
				.subscribe(() => {
					this.isLoggingOut = false;
					this.cartFacade.cleanCartCache();
					this.appearanceFacade.loadedTheme = false;
					this.appearanceFacade.logo = '';
					this._router.navigate(['auth']);
				})
		);
	}

	navigateToSettings() {
		if (!this.settingsButton) return;
		this._router.navigate(this.settingsButton.routerLink);
	}

	openCart() {
		return this._router.navigate(['account', 'cart']);
	}

	searchProduct(search: string, hideOverlay = false) {
		this.search = search;
		if (!search) return;

		if (hideOverlay) this.autocompleteOverlayRef.hide();

		this._router.navigate(['/'], {
			queryParams: { search, page: 1, category: undefined },
			queryParamsHandling: 'merge',
		});
	}

	get showDashboard() {
		return this.tenantsFacade.hasFeatures('dashboard');
	}
}
